import { Button, Center, Flex, HStack, Spinner, Text } from '@chakra-ui/react';
import { PageHeading } from '@duke-hq/ui/components/PageHeading';
import { useCallback, useMemo, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Trans, Plural } from '@lingui/react/macro';

import TableViewLayout from '@duke-hq/ui/components/TableViewLayout/TableViewLayout';
import { usePagination } from '@duke-hq/ui/components/hooks/usePagination';
import { Pagination } from '@duke-hq/ui/components/Pagination/Pagination';
import { emptyPaginationResponse, SortOrder } from '@duke-hq/lib/pagination';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { withPermission } from '../withPermission';
import { sizeVars } from '@duke-hq/ui/constants/layout';
import { SupportTasksList } from './SupportTasksList';
import { SupportTaskFilters } from './SupportTaskFilters';
import { SupportTaskModal } from './SupportTaskModal';
import { CompleteTaskModal } from './CompleteTaskModal';
import { CancelTaskModal } from './CancelTaskModal';
import { BulkAssignModal } from './BulkAssignModal';
import {
  SupportTaskProcessor,
  SupportTaskStatus,
  SupportTaskKind,
  States,
} from '@duke-hq/svc-api-db/client';
import pickBy from 'lodash-es/pickBy.js';

interface SortFilters {
  orderBy?: string;
  sort?: SortOrder;
  page?: string;
  processor?: SupportTaskProcessor;
  status?: SupportTaskStatus;
  kind?: SupportTaskKind;
  assignedTo?: string;
  providerIds?: string;
  state?: string;
}

export const SupportTasksPage = withPermission('canViewSupportTasks', () => {
  const { taskId: urlTaskId } = useParams<{ taskId?: string }>();
  const navigate = useNavigate();
  const { currentPage, pageSize, setPage } = usePagination();
  const [selectedTaskId, setSelectedTaskId] = useState<string | undefined>(urlTaskId);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [selectedTaskIds, setSelectedTaskIds] = useState<string[]>([]);
  const [isBulkAssignModalOpen, setIsBulkAssignModalOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams({
    orderBy: 'processAnytimeAfter',
    sort: SortOrder.ASC,
    page: '1',
  });

  const getURLFilters = useMemo(() => {
    const urlFilters = Object.fromEntries(searchParams.entries());
    const { page, processor, status, kind, assignedTo, orderBy, sort, providerIds, state } =
      urlFilters;

    const isValidProcessor = (value: string): value is SupportTaskProcessor => {
      const processors: readonly string[] = Object.values(SupportTaskProcessor);
      return processors.includes(value);
    };

    const isValidStatus = (value: string): value is SupportTaskStatus => {
      const statuses: readonly string[] = Object.values(SupportTaskStatus);
      return statuses.includes(value);
    };

    const isValidKind = (value: string): value is SupportTaskKind => {
      const kinds: readonly string[] = Object.values(SupportTaskKind);
      return kinds.includes(value);
    };

    const isValidSortOrder = (value: string): value is SortOrder => {
      const sortOrders: readonly string[] = Object.values(SortOrder);
      return sortOrders.includes(value);
    };

    return {
      processor:
        processor != null && processor !== '' && isValidProcessor(processor)
          ? processor
          : undefined,
      status: status != null && status !== '' && isValidStatus(status) ? status : undefined,
      kind: kind != null && kind !== '' && isValidKind(kind) ? kind : undefined,
      assignedTo: assignedTo != null && assignedTo !== '' ? assignedTo : undefined,
      providerIds: providerIds != null && providerIds !== '' ? providerIds : undefined,
      state: state != null && state !== '' ? state : undefined,
      page: page != null && page !== '' ? page : undefined,
      orderBy: orderBy != null && orderBy !== '' ? orderBy : undefined,
      sort: sort != null && sort !== '' && isValidSortOrder(sort) ? sort : undefined,
    };
  }, [searchParams]);

  const { data: tasks, isFetching } = trpcAdmin.support.task.list.useQuery(
    {
      filters: {
        processor: getURLFilters.processor,
        status: getURLFilters.status,
        kind: getURLFilters.kind,
        assignedToDrns: getURLFilters.assignedTo?.split(',').filter(Boolean),
        providerIds: getURLFilters.providerIds
          ?.split(',')
          .map(Number)
          .filter((id) => !Number.isNaN(id)),
        states: getURLFilters.state
          ?.split(',')
          .filter((s): s is States => Object.values(States).includes(s as States)),
        sort: getURLFilters.sort,
        orderBy: getURLFilters.orderBy,
      },
      pagination: {
        page: Number(currentPage),
        limit: pageSize,
      },
    },
    {
      initialData: emptyPaginationResponse(),
      placeholderData: (previousData) => previousData,
    },
  );

  const { data: filterOptions } = trpcAdmin.support.task.filterOptions.useQuery(undefined, {
    initialData: {
      availableStates: [],
      availableProviderIds: [],
      availableAssigneeDrns: [],
      hasUnassignedTasks: true,
    },
  });

  const { data: selectedTask } = trpcAdmin.support.task.get.useQuery(
    { taskId: selectedTaskId ?? '' },
    { enabled: selectedTaskId != null && selectedTaskId !== '' },
  );

  const handleFilterChange = useCallback(
    (newFilters: Partial<SortFilters>) => {
      const updatedFilters = pickBy(
        { ...Object.fromEntries(searchParams.entries()), ...newFilters, page: undefined },
        (value) => value != null && value !== '',
      );

      const newSearchParams = new URLSearchParams(updatedFilters as Record<string, string>);
      setSearchParams(newSearchParams, { replace: true });

      // Clear selected tasks when filters change to avoid confusion
      setSelectedTaskIds([]);
    },
    [searchParams, setSearchParams],
  );

  const handleTaskClick = useCallback(
    (taskId: string) => {
      setSelectedTaskId(taskId);
      void navigate(
        { pathname: `/tasks/${taskId}`, search: searchParams.toString() },
        { replace: true },
      );
    },
    [navigate, searchParams],
  );

  const handleCloseModal = useCallback(() => {
    setSelectedTaskId(undefined);
    void navigate({ pathname: '/tasks', search: searchParams.toString() }, { replace: true });
  }, [navigate, searchParams]);

  const handleOpenCompleteModal = useCallback(() => {
    setIsCompleteModalOpen(true);
  }, []);

  const handleCloseCompleteModal = useCallback(() => {
    setIsCompleteModalOpen(false);
  }, []);

  const handleOpenCancelModal = useCallback(() => {
    setIsCancelModalOpen(true);
  }, []);

  const handleCloseCancelModal = useCallback(() => {
    setIsCancelModalOpen(false);
  }, []);

  const handleSelectionChange = useCallback((taskIds: string[]) => {
    setSelectedTaskIds(taskIds);
  }, []);

  const handleOpenBulkAssignModal = useCallback(() => {
    setIsBulkAssignModalOpen(true);
  }, []);

  const handleCloseBulkAssignModal = useCallback(() => {
    setIsBulkAssignModalOpen(false);
    setSelectedTaskIds([]);
  }, []);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
      setSelectedTaskIds([]);
    },
    [setPage],
  );

  return (
    <>
      <Flex h={`calc(100vh - ${sizeVars.navbar})`} flexDir="row">
        <TableViewLayout
          pageHeader={[
            <PageHeading key="support-tasks">
              <Trans>Support Tasks</Trans>
            </PageHeading>,
          ]}
          filters={
            <SupportTaskFilters
              filters={getURLFilters}
              onChange={handleFilterChange}
              availableStates={filterOptions?.availableStates}
              availableProviderIds={filterOptions?.availableProviderIds}
              availableAssigneeDrns={filterOptions?.availableAssigneeDrns}
              hasUnassignedTasks={filterOptions?.hasUnassignedTasks}
            />
          }
          tableActionsLeft={
            <HStack spacing={4}>
              <Text fontSize="md" fontWeight="semibold" color="fg.subtle">
                <Plural
                  value={tasks?.pagination.totalCount || 0}
                  zero="0 tasks"
                  one="1 task"
                  other="# tasks"
                />
              </Text>
              {selectedTaskIds.length > 0 && (
                <Button size="sm" colorScheme="blue" onClick={handleOpenBulkAssignModal}>
                  <Trans>Assign {selectedTaskIds.length} tasks</Trans>
                </Button>
              )}
            </HStack>
          }
          table={
            <>
              <SupportTasksList
                tasks={tasks.items}
                urlParams={getURLFilters}
                onFilters={handleFilterChange}
                onTaskClick={handleTaskClick}
                selectedTaskIds={selectedTaskIds}
                onSelectionChange={handleSelectionChange}
              />
              {isFetching ? (
                <Center p={8}>
                  <Spinner size="xl" />
                </Center>
              ) : tasks.items.length === 0 ? (
                <Center p={8}>
                  <Text textAlign="center">
                    <Trans>No tasks found</Trans>
                  </Text>
                </Center>
              ) : null}
            </>
          }
          pagination={
            tasks.items.length > 0 ? (
              <Pagination pagination={tasks.pagination} onPageChange={handlePageChange} />
            ) : null
          }
        />
      </Flex>

      <SupportTaskModal
        isOpen={selectedTaskId != null && selectedTaskId !== ''}
        onClose={handleCloseModal}
        taskId={selectedTaskId}
        onComplete={handleOpenCompleteModal}
        onCancel={handleOpenCancelModal}
      />

      {selectedTaskId != null && selectedTaskId !== '' && selectedTask?.kind != null ? (
        <CompleteTaskModal
          isOpen={isCompleteModalOpen}
          onClose={handleCloseCompleteModal}
          taskId={selectedTaskId}
          taskKind={selectedTask.kind}
        />
      ) : null}

      {selectedTaskId != null && selectedTaskId !== '' ? (
        <CancelTaskModal
          isOpen={isCancelModalOpen}
          onClose={handleCloseCancelModal}
          taskId={selectedTaskId}
        />
      ) : null}

      <BulkAssignModal
        isOpen={isBulkAssignModalOpen}
        onClose={handleCloseBulkAssignModal}
        taskIds={selectedTaskIds}
      />
    </>
  );
});
